import { useState, useEffect, useCallback } from "react";
import { useClub } from "@/pages/ClubLayout";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trophy, X, ArrowLeft, Pencil, Calendar, Layers, Hash, Upload, Download, Search, Trash2 } from "lucide-react";
import { ConfirmDeleteModal } from "@/components/ConfirmDeleteModal";
import { CsvImportButton } from "@/components/CsvImport";

const AUTH = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("cadii_club_token") || ""}`,
});

const STATUS_MAP = {
  draft: { label: "Borrador", cls: "bg-gray-100 text-gray-600" },
  active: { label: "Activo", cls: "bg-green-100 text-green-700" },
  completed: { label: "Finalizado", cls: "bg-blue-100 text-blue-700" },
  archived: { label: "Archivado", cls: "bg-gray-100 text-gray-400" },
};

export default function ClubRankings() {
  const ctx = useClub();
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list"); // list | create | detail
  const [selectedRanking, setSelectedRanking] = useState(null);
  const [rankingToDelete, setRankingToDelete] = useState(null);

  const fetchRankings = useCallback(async () => {
    try {
      const res = await fetch(`${API}/club/rankings`, { credentials: "include", headers: AUTH() });
      if (res.ok) setRankings(await res.json());
    } catch {
      toast.error("Error al cargar rankings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRankings(); }, [fetchRankings]);

  const openDetail = async (id) => {
    try {
      const res = await fetch(`${API}/club/rankings/${id}`, { credentials: "include", headers: AUTH() });
      if (res.ok) { setSelectedRanking(await res.json()); setView("detail"); }
    } catch { toast.error("Error"); }
  };

  const handleDelete = (e, r) => {
    e.stopPropagation();
    setRankingToDelete(r);
  };

  const confirmDelete = async () => {
    const r = rankingToDelete;
    if (!r) return;
    const res = await fetch(`${API}/club/rankings/${r.ranking_id}`, { method: "DELETE", credentials: "include", headers: AUTH() });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { toast.error(data.detail || "Error al eliminar"); return; }
    toast.success("Ranking eliminado");
    setRankingToDelete(null);
    fetchRankings();
  };

  if (view === "create") {
    return <CreateRanking onBack={() => setView("list")} onCreated={() => { setView("list"); fetchRankings(); }} />;
  }
  if (view === "detail" && selectedRanking) {
    return <RankingDetail ranking={selectedRanking} onBack={() => { setView("list"); setSelectedRanking(null); }} onUpdated={(r) => { setSelectedRanking(r); fetchRankings(); }} />;
  }

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] flex items-center gap-2" data-testid="club-rankings-title">
            <Trophy className="w-6 h-6 text-[#1B4D3E]" strokeWidth={1.5} />
            Rankings
          </h1>
          <p className="text-sm text-[#64748B] mt-1">{ctx?.club?.name}</p>
        </div>
        <Button onClick={() => setView("create")} className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" data-testid="create-ranking-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Ranking
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" /></div>
      ) : rankings.length === 0 ? (
        <Card className="card-default">
          <CardContent className="py-16 text-center">
            <Trophy className="w-12 h-12 text-[#64748B] mx-auto mb-4 opacity-40" />
            <p className="text-[#64748B] font-medium mb-1">No hay rankings creados</p>
            <p className="text-sm text-[#94A3B8] mb-4">Crea tu primer ranking para empezar a organizar competencias</p>
            <Button onClick={() => setView("create")} className="bg-[#1B4D3E] hover:bg-[#163F33] text-white">
              <Plus className="w-4 h-4 mr-2" /> Crear Ranking
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="rankings-list">
          {rankings.map((r) => {
            const st = STATUS_MAP[r.status] || STATUS_MAP.draft;
            return (
              <Card key={r.ranking_id} className="card-default cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDetail(r.ranking_id)} data-testid={`ranking-${r.ranking_id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-bold text-[#0F172A] text-lg">{r.name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.cls}`}>{st.label}</span>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-[#64748B]">
                        {r.season && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {r.season}</span>}
                        <span className="flex items-center gap-1"><Layers className="w-3.5 h-3.5" /> {r.stage_count || 0} etapas</span>
                        <span className="flex items-center gap-1"><Hash className="w-3.5 h-3.5" /> {r.categories?.length || 0} categorias</span>
                      </div>
                      {r.categories?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {r.categories.map((c) => (
                            <span key={c.category_id} className="text-xs bg-[#1B4D3E]/10 text-[#1B4D3E] px-2 py-0.5 rounded-full">{c.name}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => handleDelete(e, r)}
                      className="text-red-600 border-red-200 hover:bg-red-50 shrink-0"
                      data-testid={`delete-ranking-${r.ranking_id}`}
                      title="Eliminar ranking"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <ConfirmDeleteModal
        open={!!rankingToDelete}
        onOpenChange={(v) => { if (!v) setRankingToDelete(null); }}
        entityName={rankingToDelete?.name}
        entityLabel="ranking"
        description="Esta acción es permanente y no se puede deshacer."
        cascade={["Etapas del ranking", "Inscripciones de jugadores", "Tee times generados", "Resultados capturados"]}
        onConfirm={confirmDelete}
        testId="delete-ranking-modal"
      />
    </div>
  );
}


// ==========================================================================
// Create Ranking
// ==========================================================================

function CreateRanking({ onBack, onCreated }) {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    name: "", season: new Date().getFullYear().toString(), start_date: "", end_date: "", estimated_stages: "",
    category_ids: [], points_table: null, categories_mode: "by_category", convocatoria: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      const res = await fetch(`${API}/club/categories`, { credentials: "include", headers: AUTH() });
      if (res.ok) {
        const cats = await res.json();
        setCategories(cats.filter((c) => c.status === "active"));
      }
    };
    load();
  }, []);

  const toggleCat = (catId) => {
    setForm((prev) => ({
      ...prev,
      category_ids: prev.category_ids.includes(catId)
        ? prev.category_ids.filter((id) => id !== catId)
        : [...prev.category_ids, catId],
    }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return toast.error("Nombre requerido");
    if (form.categories_mode === "by_category" && form.category_ids.length === 0) return toast.error("Selecciona al menos una categoria");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        season: form.season.trim() || null,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        estimated_stages: form.estimated_stages ? parseInt(form.estimated_stages) : null,
        category_ids: form.categories_mode === "single" ? [] : form.category_ids,
        categories_mode: form.categories_mode,
        convocatoria: form.convocatoria,
      };
      const res = await fetch(`${API}/club/rankings`, {
        method: "POST", credentials: "include", headers: AUTH(), body: JSON.stringify(payload),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || "Error"); }
      toast.success("Ranking creado");
      onCreated();
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Error al crear");
    } finally { setSaving(false); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-3xl mx-auto">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-[#64748B] hover:text-[#0F172A] mb-6" data-testid="back-to-rankings">
        <ArrowLeft className="w-4 h-4" /> Volver a Rankings
      </button>
      <h1 className="text-2xl font-bold text-[#0F172A] mb-6" data-testid="create-ranking-title">Nuevo Ranking</h1>

      {/* General info */}
      <Card className="card-default mb-6">
        <CardContent className="p-6">
          <h2 className="text-base font-bold text-[#0F172A] mb-4">Informacion General</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-sm font-medium">Nombre del Ranking *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder='Ej: "Ranking General 2026"' data-testid="ranking-name" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Temporada / Año</Label>
              <Input value={form.season} onChange={(e) => setForm({ ...form, season: e.target.value })} placeholder="2026" data-testid="ranking-season" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Etapas Estimadas</Label>
              <Input type="number" value={form.estimated_stages} onChange={(e) => setForm({ ...form, estimated_stages: e.target.value })} placeholder="Ej: 12" data-testid="ranking-stages" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Fecha Inicio</Label>
              <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="ranking-start" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Fecha Cierre</Label>
              <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="ranking-end" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Convocatoria */}
      <Card className="card-default mb-6">
        <CardContent className="p-6">
          <h2 className="text-base font-bold text-[#0F172A] mb-2">Convocatoria</h2>
          <p className="text-xs text-[#94A3B8] mb-3">Texto público con las bases del ranking (visible en el micrositio del club)</p>
          <textarea value={form.convocatoria} onChange={(e) => setForm({ ...form, convocatoria: e.target.value })}
            rows={5} placeholder="Ej: Fechas, formato de juego, premios, requisitos de inscripción..."
            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4D3E]/20 focus:border-[#1B4D3E] resize-y"
            data-testid="convocatoria-input" />
        </CardContent>
      </Card>

      {/* Categories mode */}
      <Card className="card-default mb-6">
        <CardContent className="p-6">
          <h2 className="text-base font-bold text-[#0F172A] mb-2">Modalidad de Competencia</h2>
          <p className="text-xs text-[#94A3B8] mb-4">Define si los jugadores compiten separados por categoría o todos juntos</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button onClick={() => setForm({ ...form, categories_mode: "by_category" })}
              className={`p-4 rounded-xl border-2 text-left transition-all ${form.categories_mode === "by_category" ? "border-[#1B4D3E] bg-[#1B4D3E]/5" : "border-gray-200"}`}
              data-testid="mode-by-category">
              <p className="text-sm font-bold text-[#0F172A]">Por Categorías</p>
              <p className="text-xs text-[#64748B] mt-1">Cada jugador compite en su categoría según su handicap</p>
            </button>
            <button onClick={() => setForm({ ...form, categories_mode: "single" })}
              className={`p-4 rounded-xl border-2 text-left transition-all ${form.categories_mode === "single" ? "border-[#1B4D3E] bg-[#1B4D3E]/5" : "border-gray-200"}`}
              data-testid="mode-single">
              <p className="text-sm font-bold text-[#0F172A]">Categoría Única</p>
              <p className="text-xs text-[#64748B] mt-1">Todos los jugadores compiten juntos en una sola tabla</p>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Categories */}
      {form.categories_mode === "by_category" && (
      <Card className="card-default mb-6">
        <CardContent className="p-6">
          <h2 className="text-base font-bold text-[#0F172A] mb-2">Categorias Activas *</h2>
          <p className="text-xs text-[#94A3B8] mb-4">Selecciona las categorias que participaran en este ranking</p>
          {categories.length === 0 ? (
            <p className="text-sm text-[#94A3B8]">No hay categorias configuradas. Ve a Categorias para crear.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="ranking-categories">
              {categories.map((cat) => {
                const selected = form.category_ids.includes(cat.category_id);
                return (
                  <button
                    key={cat.category_id}
                    type="button"
                    onClick={() => toggleCat(cat.category_id)}
                    className={`flex items-center justify-between p-3 rounded-lg border text-left text-sm transition-all ${
                      selected ? "border-[#1B4D3E] bg-[#1B4D3E]/5 text-[#1B4D3E]" : "border-gray-200 text-[#64748B] hover:border-gray-300"
                    }`}
                    data-testid={`cat-toggle-${cat.category_id}`}
                  >
                    <div>
                      <span className="font-semibold">{cat.name}</span>
                      <span className="ml-2 text-xs">
                        {cat.min_index == null ? "≤" : cat.min_index} — {cat.max_index ?? "∞"}
                      </span>
                    </div>
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${selected ? "border-[#1B4D3E] bg-[#1B4D3E]" : "border-gray-300"}`}>
                      {selected && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Points table info */}
      <Card className="card-default mb-6">
        <CardContent className="p-6">
          <h2 className="text-base font-bold text-[#0F172A] mb-2">Tabla de Puntos</h2>
          <p className="text-xs text-[#94A3B8] mb-3">Se usara la tabla de puntos base. Podras editarla despues desde el detalle del ranking.</p>
          <div className="grid grid-cols-5 gap-2 text-xs text-center">
            {[1,2,3,4,5,6,7,8,9,10].map((pos) => (
              <div key={pos} className="bg-gray-50 rounded-lg p-2">
                <p className="text-[#94A3B8]">{pos}o</p>
                <p className="font-bold text-[#0F172A]">{[100,85,75,68,62,57,53,50,47,45][pos-1]}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-3">
        <Button variant="outline" className="flex-1" onClick={onBack}>Cancelar</Button>
        <Button className="flex-1 bg-[#1B4D3E] hover:bg-[#163F33] text-white" onClick={handleSubmit} disabled={saving} data-testid="save-ranking-btn">
          {saving ? "Creando..." : "Crear Ranking"}
        </Button>
      </div>
    </div>
  );
}


// ==========================================================================
// Ranking Detail
// ==========================================================================

function RankingDetail({ ranking, onBack, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: ranking.name, season: ranking.season || "", start_date: ranking.start_date || "",
    end_date: ranking.end_date || "", status: ranking.status,
    categories_mode: ranking.categories_mode || "by_category",
    convocatoria: ranking.convocatoria || "",
  });
  const [editingPoints, setEditingPoints] = useState(false);
  const [pointsTable, setPointsTable] = useState(ranking.points_table || []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (editingPoints) payload.points_table = pointsTable;
      const res = await fetch(`${API}/club/rankings/${ranking.ranking_id}`, {
        method: "PUT", credentials: "include", headers: AUTH(), body: JSON.stringify(payload),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || "Error"); }
      const updated = await res.json();
      toast.success("Ranking actualizado");
      setEditing(false);
      setEditingPoints(false);
      onUpdated(updated);
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Error al guardar");
    } finally { setSaving(false); }
  };

  const st = STATUS_MAP[ranking.status] || STATUS_MAP.draft;

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-[#64748B] hover:text-[#0F172A] mb-6" data-testid="back-to-rankings-detail">
        <ArrowLeft className="w-4 h-4" /> Volver a Rankings
      </button>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A]" data-testid="ranking-detail-title">{ranking.name}</h1>
          <div className="flex items-center gap-3 mt-1">
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${st.cls}`}>{st.label}</span>
            {ranking.season && <span className="text-sm text-[#64748B]">{ranking.season}</span>}
            <span className="text-sm text-[#64748B]">{ranking.stage_count || 0} etapas</span>
          </div>
        </div>
        {!editing && (
          <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-ranking-btn">
            <Pencil className="w-4 h-4 mr-1" /> Editar
          </Button>
        )}
      </div>

      {/* General info */}
      <Card className="card-default mb-6">
        <CardContent className="p-6">
          <h2 className="text-base font-bold text-[#0F172A] mb-4">Informacion General</h2>
          {editing ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2 sm:col-span-2">
                <Label>Nombre</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-ranking-name" />
              </div>
              <div className="space-y-2"><Label>Temporada</Label><Input value={form.season} onChange={(e) => setForm({ ...form, season: e.target.value })} /></div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="edit-ranking-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Borrador</SelectItem>
                    <SelectItem value="active">Activo</SelectItem>
                    <SelectItem value="completed">Finalizado</SelectItem>
                    <SelectItem value="archived">Archivado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Fecha Inicio</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
              <div className="space-y-2"><Label>Fecha Cierre</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
              <div className="space-y-2">
                <Label>Modalidad de Competencia</Label>
                <Select value={form.categories_mode} onValueChange={(v) => setForm({ ...form, categories_mode: v })}>
                  <SelectTrigger data-testid="edit-ranking-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="by_category">Por Categorías</SelectItem>
                    <SelectItem value="single">Categoría Única</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-[#94A3B8]">Aplica a las próximas inscripciones</p>
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Convocatoria (pública)</Label>
                <textarea value={form.convocatoria} onChange={(e) => setForm({ ...form, convocatoria: e.target.value })}
                  rows={4} placeholder="Bases del ranking, premios, requisitos..."
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4D3E]/20 resize-y"
                  data-testid="edit-convocatoria-input" />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-y-3 gap-x-8 text-sm">
              <InfoRow label="Nombre" value={ranking.name} />
              <InfoRow label="Temporada" value={ranking.season} />
              <InfoRow label="Inicio" value={ranking.start_date} />
              <InfoRow label="Cierre" value={ranking.end_date} />
              <InfoRow label="Etapas estimadas" value={ranking.estimated_stages} />
              <InfoRow label="Modalidad" value={ranking.categories_mode === "single" ? "Categoría Única" : "Por Categorías"} />
              <InfoRow label="Status" value={st.label} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Categories */}
      <Card className="card-default mb-6">
        <CardContent className="p-6">
          <h2 className="text-base font-bold text-[#0F172A] mb-3">Categorias del Ranking</h2>
          {ranking.categories?.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="ranking-categories-detail">
              {ranking.categories.map((c) => (
                <div key={c.category_id} className="flex items-center justify-between p-3 bg-[#1B4D3E]/5 rounded-lg border border-[#1B4D3E]/20">
                  <span className="font-semibold text-sm text-[#1B4D3E]">{c.name}</span>
                  <span className="text-xs text-[#64748B]">
                    {c.min_index == null ? "≤" : c.min_index} — {c.max_index ?? "∞"}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[#94A3B8]">Sin categorias asignadas</p>
          )}
        </CardContent>
      </Card>

      {/* Points table */}
      <Card className="card-default mb-6">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-bold text-[#0F172A]">Tabla de Puntos</h2>
            {!editingPoints && !editing && (
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => setEditingPoints(true)} data-testid="edit-points-btn">
                  <Pencil className="w-4 h-4 mr-1" /> Editar
                </Button>
              </div>
            )}
          </div>
          {editingPoints ? (
            <div className="space-y-3">
              {/* CSV import/export controls */}
              <div className="flex flex-wrap gap-2 mb-3 pb-3 border-b border-gray-100">
                <Button
                  variant="outline" size="sm"
                  onClick={() => {
                    const csv = "position,points\n" + pointsTable.map((r) => `${r.position},${r.points}`).join("\n") + "\n";
                    const blob = new Blob([csv], { type: "text/csv" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a"); a.href = url; a.download = "tabla_puntos.csv"; a.click();
                    URL.revokeObjectURL(url);
                  }}
                  data-testid="download-points-csv"
                >
                  <Download className="w-3.5 h-3.5 mr-1" /> Descargar CSV
                </Button>
                <label className="inline-flex items-center gap-1 text-sm text-[#1B4D3E] cursor-pointer border border-gray-200 rounded-md px-3 py-1.5 hover:bg-gray-50">
                  <Upload className="w-3.5 h-3.5" /> Importar CSV
                  <input
                    type="file" accept=".csv,.txt" className="hidden"
                    data-testid="import-points-csv"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = (ev) => {
                        const lines = (ev.target.result || "").trim().split("\n").filter(Boolean);
                        const parsed = [];
                        for (let i = 0; i < lines.length; i++) {
                          const parts = lines[i].split(",").map((s) => s.trim());
                          const pos = parseInt(parts[0]);
                          const pts = parseInt(parts[1]);
                          if (!isNaN(pos) && !isNaN(pts) && pos > 0) {
                            parsed.push({ position: pos, points: pts });
                          }
                        }
                        if (parsed.length > 0) {
                          parsed.sort((a, b) => a.position - b.position);
                          setPointsTable(parsed);
                          toast.success(`${parsed.length} posiciones importadas`);
                        } else {
                          toast.error("No se encontraron datos validos. Formato: position,points");
                        }
                      };
                      reader.readAsText(file);
                      e.target.value = "";
                    }}
                  />
                </label>
              </div>
              {pointsTable.map((row, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <span className="text-sm text-[#64748B] w-8">{row.position}o</span>
                  <Input
                    type="number"
                    value={row.points}
                    onChange={(e) => {
                      const newTable = [...pointsTable];
                      newTable[idx] = { ...row, points: parseInt(e.target.value) || 0 };
                      setPointsTable(newTable);
                    }}
                    className="w-24"
                  />
                  <span className="text-xs text-[#94A3B8]">pts</span>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setPointsTable([...pointsTable, { position: pointsTable.length + 1, points: 0 }])}>
                <Plus className="w-3 h-3 mr-1" /> Agregar posicion
              </Button>
              {/* Save button inline for points */}
              <div className="flex gap-2 pt-3 mt-3 border-t border-gray-100">
                <Button variant="outline" size="sm" onClick={() => { setEditingPoints(false); setPointsTable(ranking.points_table || []); }}>Cancelar</Button>
                <Button size="sm" className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" onClick={handleSave} disabled={saving} data-testid="save-points-inline-btn">
                  {saving ? "Guardando..." : "Guardar Puntos"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-5 sm:grid-cols-8 lg:grid-cols-10 gap-2 text-xs text-center" data-testid="points-table">
              {(ranking.points_table || []).map((row) => (
                <div key={row.position} className="bg-gray-50 rounded-lg p-2">
                  <p className="text-[#94A3B8]">{row.position}o</p>
                  <p className="font-bold text-[#0F172A]">{row.points}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stages */}
      <StagesSection ranking={ranking} onStageCreated={async () => {
        const res = await fetch(`${API}/club/rankings/${ranking.ranking_id}`, { credentials: "include", headers: AUTH() });
        if (res.ok) onUpdated(await res.json());
      }} />

      {/* Ranking Leaderboard */}
      <RankingLeaderboard rankingId={ranking.ranking_id} />

      {editing && (
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={() => setEditing(false)}>Cancelar</Button>
          <Button className="flex-1 bg-[#1B4D3E] hover:bg-[#163F33] text-white" onClick={handleSave} disabled={saving} data-testid="save-edit-ranking-btn">
            {saving ? "Guardando..." : "Guardar Cambios"}
          </Button>
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div>
      <span className="text-[#94A3B8] text-xs uppercase">{label}</span>
      <p className="text-[#0F172A]">{value || "-"}</p>
    </div>
  );
}


// ==========================================================================
// Stages Section (inside Ranking Detail)
// ==========================================================================

const STAGE_STATUS_MAP = {
  draft: { label: "Borrador", cls: "bg-gray-100 text-gray-600" },
  open: { label: "Abierta", cls: "bg-blue-100 text-blue-700" },
  locked: { label: "Cerrada", cls: "bg-amber-100 text-amber-700" },
  in_progress: { label: "En Juego", cls: "bg-green-100 text-green-700" },
  completed: { label: "Finalizada", cls: "bg-purple-100 text-purple-700" },
};

function StagesSection({ ranking, onStageCreated }) {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedStage, setSelectedStage] = useState(null);

  const fetchStages = useCallback(async () => {
    try {
      const res = await fetch(`${API}/club/rankings/${ranking.ranking_id}/stages`, {
        credentials: "include", headers: AUTH(),
      });
      if (res.ok) setStages(await res.json());
    } catch { /* silent */ } finally { setLoading(false); }
  }, [ranking.ranking_id]);

  useEffect(() => { fetchStages(); }, [fetchStages]);

  if (selectedStage) {
    return (
      <StageDetail
        stage={selectedStage}
        rankingId={ranking.ranking_id}
        onBack={() => { setSelectedStage(null); fetchStages(); }}
        onUpdated={(s) => setSelectedStage(s)}
      />
    );
  }

  return (
    <Card className="card-default mb-6">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold text-[#0F172A]">Etapas ({stages.length})</h2>
          <Button size="sm" onClick={() => setShowCreate(true)} className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" data-testid="create-stage-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Nueva Etapa
          </Button>
        </div>

        {showCreate && (
          <CreateStageForm
            rankingId={ranking.ranking_id}
            onClose={() => setShowCreate(false)}
            onCreated={() => { setShowCreate(false); fetchStages(); onStageCreated(); }}
          />
        )}

        {loading ? (
          <div className="flex justify-center py-8"><div className="w-6 h-6 border-3 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" /></div>
        ) : stages.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm text-[#94A3B8]">No hay etapas creadas</p>
            <p className="text-xs text-[#94A3B8] mt-1">Crea la primera etapa para empezar a operar</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="stages-list">
            {stages.map((s) => {
              const st = STAGE_STATUS_MAP[s.status] || STAGE_STATUS_MAP.draft;
              return (
                <div
                  key={s.stage_id}
                  className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:border-gray-200 hover:bg-gray-50/50 cursor-pointer transition-colors"
                  onClick={() => setSelectedStage(s)}
                  data-testid={`stage-${s.stage_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#1B4D3E]/10 flex items-center justify-center text-sm font-bold text-[#1B4D3E]">
                      {s.stage_number}
                    </div>
                    <div>
                      <p className="font-medium text-[#0F172A] text-sm">{s.name}</p>
                      <div className="flex items-center gap-2 text-xs text-[#94A3B8]">
                        {s.start_date && <span>{s.start_date}</span>}
                        <span>{s.play_days} dia{s.play_days > 1 ? "s" : ""}</span>
                        {s.point_multiplier !== 1.0 && <span className="text-amber-600 font-medium">x{s.point_multiplier}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#94A3B8]">{s.enrolled_count || 0} inscritos</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.cls}`}>{st.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function CreateStageForm({ rankingId, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", start_date: "", end_date: "", play_days: "1", rounds_per_day: "1",
    point_multiplier: "1.0", notes: "",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!form.name.trim()) return toast.error("Nombre requerido");
    setSaving(true);
    try {
      const res = await fetch(`${API}/club/rankings/${rankingId}/stages`, {
        method: "POST", credentials: "include", headers: AUTH(),
        body: JSON.stringify({
          name: form.name.trim(),
          start_date: form.start_date || null,
          end_date: form.end_date || null,
          play_days: parseInt(form.play_days) || 1,
          rounds_per_day: parseInt(form.rounds_per_day) || 1,
          point_multiplier: parseFloat(form.point_multiplier) || 1.0,
          notes: form.notes.trim() || null,
        }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || "Error"); }
      toast.success("Etapa creada");
      onCreated();
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Error al crear etapa");
    } finally { setSaving(false); }
  };

  return (
    <div className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-200" data-testid="create-stage-form">
      <h3 className="font-bold text-[#0F172A] mb-3">Nueva Etapa</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <div className="space-y-1 sm:col-span-2">
          <Label className="text-xs font-medium">Nombre *</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder='Ej: "Etapa 1 - Enero"' data-testid="stage-name" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs font-medium">Fecha Inicio</Label>
          <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="stage-start" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs font-medium">Fecha Cierre</Label>
          <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="stage-end" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs font-medium">Dias de Juego</Label>
          <Select value={form.play_days} onValueChange={(v) => setForm({ ...form, play_days: v })}>
            <SelectTrigger data-testid="stage-days"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4].map((d) => <SelectItem key={d} value={String(d)}>{d} dia{d > 1 ? "s" : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs font-medium">Multiplicador de Puntos</Label>
          <Select value={form.point_multiplier} onValueChange={(v) => setForm({ ...form, point_multiplier: v })}>
            <SelectTrigger data-testid="stage-multiplier"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1.0">Normal (x1)</SelectItem>
              <SelectItem value="1.5">x1.5</SelectItem>
              <SelectItem value="2.0">Doble (x2)</SelectItem>
              <SelectItem value="2.5">x2.5</SelectItem>
              <SelectItem value="3.0">Triple (x3)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1 sm:col-span-2">
          <Label className="text-xs font-medium">Notas</Label>
          <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Observaciones opcionales" data-testid="stage-notes" />
        </div>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={onClose}>Cancelar</Button>
        <Button size="sm" className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" onClick={handleSubmit} disabled={saving} data-testid="save-stage-btn">
          {saving ? "Creando..." : "Crear Etapa"}
        </Button>
      </div>
    </div>
  );
}


function StageDetail({ stage, rankingId, onBack, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: stage.name, start_date: stage.start_date || "", end_date: stage.end_date || "",
    play_days: String(stage.play_days || 1), rounds_per_day: String(stage.rounds_per_day || 1),
    point_multiplier: String(stage.point_multiplier || 1.0), notes: stage.notes || "", status: stage.status,
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/club/rankings/${rankingId}/stages/${stage.stage_id}`, {
        method: "PUT", credentials: "include", headers: AUTH(),
        body: JSON.stringify({
          ...form,
          play_days: parseInt(form.play_days) || 1,
          rounds_per_day: parseInt(form.rounds_per_day) || 1,
          point_multiplier: parseFloat(form.point_multiplier) || 1.0,
        }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || "Error"); }
      const updated = await res.json();
      toast.success("Etapa actualizada");
      setEditing(false);
      onUpdated(updated);
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Error al guardar");
    } finally { setSaving(false); }
  };

  const st = STAGE_STATUS_MAP[stage.status] || STAGE_STATUS_MAP.draft;

  return (
    <div className="mb-6" data-testid="stage-detail">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-[#64748B] hover:text-[#0F172A] mb-4" data-testid="back-to-stages">
        <ArrowLeft className="w-4 h-4" /> Volver a Etapas
      </button>

      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-[#0F172A]" data-testid="stage-detail-title">
            Etapa {stage.stage_number}: {stage.name}
          </h2>
          <div className="flex items-center gap-2 mt-1">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.cls}`}>{st.label}</span>
            {stage.point_multiplier !== 1.0 && <span className="text-xs text-amber-600 font-medium">x{stage.point_multiplier}</span>}
            <span className="text-xs text-[#94A3B8]">{stage.enrolled_count || 0} inscritos</span>
          </div>
        </div>
        {!editing && (
          <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-stage-btn">
            <Pencil className="w-4 h-4 mr-1" /> Editar
          </Button>
        )}
      </div>

      <Card className="card-default mb-4">
        <CardContent className="p-5">
          {editing ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1 sm:col-span-2"><Label className="text-xs">Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-stage-name" /></div>
              <div className="space-y-1"><Label className="text-xs">Fecha Inicio</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
              <div className="space-y-1"><Label className="text-xs">Fecha Cierre</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
              <div className="space-y-1"><Label className="text-xs">Dias</Label>
                <Select value={form.play_days} onValueChange={(v) => setForm({ ...form, play_days: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{[1,2,3,4].map((d) => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label className="text-xs">Multiplicador</Label>
                <Select value={form.point_multiplier} onValueChange={(v) => setForm({ ...form, point_multiplier: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1.0">x1</SelectItem><SelectItem value="1.5">x1.5</SelectItem>
                    <SelectItem value="2.0">x2</SelectItem><SelectItem value="2.5">x2.5</SelectItem><SelectItem value="3.0">x3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label className="text-xs">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="edit-stage-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Borrador</SelectItem><SelectItem value="open">Abierta</SelectItem>
                    <SelectItem value="locked">Cerrada</SelectItem><SelectItem value="in_progress">En Juego</SelectItem>
                    <SelectItem value="completed">Finalizada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1 sm:col-span-2"><Label className="text-xs">Notas</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <div className="sm:col-span-2 flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setEditing(false)}>Cancelar</Button>
                <Button size="sm" className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" onClick={handleSave} disabled={saving} data-testid="save-edit-stage-btn">
                  {saving ? "Guardando..." : "Guardar"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-3 gap-x-6 text-sm">
              <InfoRow label="Fecha Inicio" value={stage.start_date} />
              <InfoRow label="Fecha Cierre" value={stage.end_date} />
              <InfoRow label="Dias de Juego" value={stage.play_days} />
              <InfoRow label="Rondas/Dia" value={stage.rounds_per_day} />
              <InfoRow label="Multiplicador" value={`x${stage.point_multiplier}`} />
              <InfoRow label="Inscritos" value={stage.enrolled_count || 0} />
              {stage.notes && <div className="col-span-2 sm:col-span-3"><InfoRow label="Notas" value={stage.notes} /></div>}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Enrollments section */}
      <EnrollmentsSection stage={stage} rankingId={rankingId} onCountChanged={async () => {
        const res = await fetch(`${API}/club/rankings/${rankingId}/stages/${stage.stage_id}`, { credentials: "include", headers: AUTH() });
        if (res.ok) onUpdated(await res.json());
      }} />

      {/* Tee Times section */}
      <TeeTimesSection stage={stage} rankingId={rankingId} />

      {/* Scores + Leaderboard */}
      <ScoresSection stage={stage} rankingId={rankingId} onFinalized={async () => {
        const res = await fetch(`${API}/club/rankings/${rankingId}/stages/${stage.stage_id}`, { credentials: "include", headers: AUTH() });
        if (res.ok) onUpdated(await res.json());
      }} />
    </div>
  );
}

// ==========================================================================
// Enrollments Section
// ==========================================================================

function EnrollmentsSection({ stage, rankingId, onCountChanged }) {
  const ctx = useClub();
  const userRole = ctx?.clubUser?.club_role || "";
  const canWrite = userRole === "master" || userRole === "operador";
  const [enrollments, setEnrollments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSearch, setShowSearch] = useState(false);
  const [players, setPlayers] = useState([]);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [enrolling, setEnrolling] = useState(false);
  const [filters, setFilters] = useState({ sex: "all", senior: "all", hcpMin: "", hcpMax: "" });

  const fetchEnrollments = useCallback(async () => {
    try {
      const [enrRes, sumRes] = await Promise.all([
        fetch(`${API}/club/rankings/${rankingId}/stages/${stage.stage_id}/enrollments`, { credentials: "include", headers: AUTH() }),
        fetch(`${API}/club/rankings/${rankingId}/stages/${stage.stage_id}/enrollments/summary`, { credentials: "include", headers: AUTH() }),
      ]);
      if (enrRes.ok) setEnrollments(await enrRes.json());
      if (sumRes.ok) setSummary(await sumRes.json());
    } catch { /* silent */ } finally { setLoading(false); }
  }, [stage.stage_id, rankingId]);

  useEffect(() => { fetchEnrollments(); }, [fetchEnrollments]);

  const openSearch = async () => {
    setShowSearch(true);
    setSelectedIds(new Set());
    setSearchQ("");
    setFilters({ sex: "all", senior: "all", hcpMin: "", hcpMax: "" });
    try {
      const res = await fetch(`${API}/club/players?status=active`, { credentials: "include", headers: AUTH() });
      if (res.ok) {
        const all = await res.json();
        setPlayers(all);
        setSearchResults(all);
      }
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (!showSearch) return;
    const q = searchQ.toLowerCase().trim();
    const min = filters.hcpMin === "" ? null : parseFloat(String(filters.hcpMin).replace("+", "-"));
    const max = filters.hcpMax === "" ? null : parseFloat(String(filters.hcpMax).replace("+", "-"));
    setSearchResults(
      players.filter((p) => {
        if (q) {
          const hay = `${p.first_name} ${p.last_name} ${p.email || ""} ${p.member_number || ""}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        if (filters.sex !== "all" && (p.sex || "") !== filters.sex) return false;
        if (filters.senior === "yes" && !p.is_senior) return false;
        if (filters.senior === "no" && p.is_senior) return false;
        const hi = p.handicap_index;
        if (min != null && (hi == null || hi < min)) return false;
        if (max != null && (hi == null || hi > max)) return false;
        return true;
      })
    );
  }, [searchQ, players, showSearch, filters]);

  const enrolledMemberIds = new Set(enrollments.map((e) => e.member_id));

  const eligible = searchResults.filter((p) => !enrolledMemberIds.has(p.member_id));
  const allEligibleSelected = eligible.length > 0 && eligible.every((p) => selectedIds.has(p.member_id));

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (allEligibleSelected) {
        const next = new Set(prev);
        eligible.forEach((p) => next.delete(p.member_id));
        return next;
      }
      const next = new Set(prev);
      eligible.forEach((p) => next.add(p.member_id));
      return next;
    });
  };

  const enrollAllFiltered = async () => {
    if (eligible.length === 0) return;
    setSelectedIds(new Set(eligible.map((p) => p.member_id)));
    setEnrolling(true);
    try {
      const res = await fetch(`${API}/club/rankings/${rankingId}/stages/${stage.stage_id}/enrollments/bulk`, {
        method: "POST", credentials: "include", headers: AUTH(),
        body: JSON.stringify({ member_ids: eligible.map((p) => p.member_id) }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.enrolled} jugador(es) inscrito(s)${data.skipped ? `, ${data.skipped} omitido(s)` : ""}`);
        setShowSearch(false);
        fetchEnrollments();
        onCountChanged();
      }
    } catch { toast.error("Error al inscribir"); }
    finally { setEnrolling(false); }
  };

  const togglePlayer = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleBulkEnroll = async () => {
    if (selectedIds.size === 0) return;
    setEnrolling(true);
    try {
      const res = await fetch(`${API}/club/rankings/${rankingId}/stages/${stage.stage_id}/enrollments/bulk`, {
        method: "POST", credentials: "include", headers: AUTH(),
        body: JSON.stringify({ member_ids: Array.from(selectedIds) }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.enrolled} jugador(es) inscrito(s)${data.skipped ? `, ${data.skipped} omitido(s)` : ""}`);
        setShowSearch(false);
        fetchEnrollments();
        onCountChanged();
      }
    } catch { toast.error("Error al inscribir"); }
    finally { setEnrolling(false); }
  };

  const handleRemove = async (enrollmentId) => {
    try {
      const res = await fetch(`${API}/club/rankings/${rankingId}/stages/${stage.stage_id}/enrollments/${enrollmentId}`, {
        method: "DELETE", credentials: "include", headers: AUTH(),
      });
      if (res.ok) {
        toast.success("Inscripcion eliminada");
        fetchEnrollments();
        onCountChanged();
      }
    } catch { toast.error("Error"); }
  };

  const handleCsvEnroll = async (csvText) => {
    try {
      const res = await fetch(`${API}/club/rankings/${rankingId}/stages/${stage.stage_id}/enrollments/csv`, {
        method: "POST", credentials: "include", headers: AUTH(),
        body: JSON.stringify({ csv: csvText }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error al importar");
      toast.success(`${data.enrolled} inscrito(s)${data.created_members ? `, ${data.created_members} nuevo(s) en padrón` : ""}${data.already_enrolled ? `, ${data.already_enrolled} ya inscrito(s)` : ""}`);
      if (data.errors?.length) toast.warning(`${data.errors.length} fila(s) con error: ${data.errors[0]}`);
      fetchEnrollments();
      onCountChanged();
      return true;
    } catch (err) {
      toast.error(err.message);
      return false;
    }
  };

  return (
    <Card className="card-default mb-4">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-[#0F172A]">Inscripciones ({enrollments.length})</h3>
          {canWrite && (
            <div className="flex gap-2">
              <CsvImportButton
                label="Subir CSV"
                columnsHelp="Columnas aceptadas: nombre (o jugador), apellido, email, socio, handicap. Solo el nombre es obligatorio. Se buscan en el padrón por email, No. de socio o nombre; si no existen, se dan de alta automáticamente."
                templates={[{ label: "Descargar plantilla", filename: "inscritos_plantilla.csv", content: "nombre,apellido,email,socio,handicap\nJuan,Perez,juan@ejemplo.com,1024,12.4\nMaria,Lopez,,2048,8.1\n" }]}
                onImport={handleCsvEnroll}
                testId="enroll-csv"
              />
              <Button size="sm" onClick={openSearch} className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" data-testid="open-enroll-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Inscribir Jugadores
              </Button>
            </div>
          )}
        </div>

        {/* Category summary */}
        {summary && summary.total > 0 && (
          <div className="flex flex-wrap gap-2 mb-4" data-testid="enrollment-summary">
            {Object.entries(summary.by_category).map(([cat, count]) => (
              <span key={cat} className="text-xs bg-[#1B4D3E]/10 text-[#1B4D3E] px-2.5 py-1 rounded-full font-medium">
                {cat}: {count}
              </span>
            ))}
          </div>
        )}

        {/* Search/enroll modal */}
        {showSearch && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[85vh] flex flex-col" data-testid="enroll-modal">
              <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
                <h3 className="font-bold text-[#0F172A]">Inscribir Jugadores</h3>
                <button onClick={() => setShowSearch(false)}><X className="w-5 h-5 text-[#64748B]" /></button>
              </div>

              {/* Search + filters */}
              <div className="px-5 py-3 border-b border-gray-100 space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                  <Input value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Buscar por nombre, email o No. socio..." className="pl-10" data-testid="enroll-search" />
                </div>

                <div className="flex flex-wrap gap-2 items-center" data-testid="enroll-filters">
                  {/* Sexo */}
                  <div className="flex items-center gap-1 rounded-full bg-gray-100 p-0.5">
                    {[{ k: "all", l: "Todos" }, { k: "M", l: "Hombres" }, { k: "F", l: "Damas" }].map((o) => (
                      <button
                        key={o.k}
                        onClick={() => setFilters((f) => ({ ...f, sex: o.k }))}
                        className={`text-xs px-3 py-1 rounded-full transition-colors ${filters.sex === o.k ? "bg-white shadow-sm text-[#1B4D3E] font-semibold" : "text-[#64748B]"}`}
                        data-testid={`filter-sex-${o.k}`}
                      >
                        {o.l}
                      </button>
                    ))}
                  </div>
                  {/* Senior */}
                  <div className="flex items-center gap-1 rounded-full bg-gray-100 p-0.5">
                    {[{ k: "all", l: "Todos" }, { k: "yes", l: "Senior" }, { k: "no", l: "No senior" }].map((o) => (
                      <button
                        key={o.k}
                        onClick={() => setFilters((f) => ({ ...f, senior: o.k }))}
                        className={`text-xs px-3 py-1 rounded-full transition-colors ${filters.senior === o.k ? "bg-white shadow-sm text-[#1B4D3E] font-semibold" : "text-[#64748B]"}`}
                        data-testid={`filter-senior-${o.k}`}
                      >
                        {o.l}
                      </button>
                    ))}
                  </div>
                  {/* HCP range */}
                  <div className="flex items-center gap-1 text-xs text-[#64748B]">
                    <span>HCP</span>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={filters.hcpMin}
                      onChange={(e) => setFilters((f) => ({ ...f, hcpMin: e.target.value }))}
                      placeholder="min"
                      className="h-7 w-14 text-xs px-1.5"
                      data-testid="filter-hcp-min"
                    />
                    <span>–</span>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={filters.hcpMax}
                      onChange={(e) => setFilters((f) => ({ ...f, hcpMax: e.target.value }))}
                      placeholder="max"
                      className="h-7 w-14 text-xs px-1.5"
                      data-testid="filter-hcp-max"
                    />
                  </div>
                  {(filters.sex !== "all" || filters.senior !== "all" || filters.hcpMin || filters.hcpMax) && (
                    <button
                      onClick={() => setFilters({ sex: "all", senior: "all", hcpMin: "", hcpMax: "" })}
                      className="text-xs text-red-600 font-medium hover:underline"
                      data-testid="filter-clear"
                    >
                      Limpiar
                    </button>
                  )}
                </div>
              </div>

              {/* Select-all bar */}
              {eligible.length > 0 && (
                <div className="flex items-center justify-between px-5 py-2.5 bg-gray-50 border-b border-gray-100">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={allEligibleSelected}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-gray-300 text-[#1B4D3E]"
                      data-testid="enroll-select-all"
                    />
                    <span className="text-xs font-semibold text-[#0F172A]">
                      Seleccionar todos <span className="text-[#94A3B8] font-normal">({eligible.length} disponibles)</span>
                    </span>
                  </label>
                  {selectedIds.size > 0 && (
                    <span className="text-xs text-[#1B4D3E] font-semibold">{selectedIds.size} marcado(s)</span>
                  )}
                </div>
              )}

              <div className="flex-1 overflow-y-auto px-5 py-2">
                {searchResults.map((p) => {
                  const alreadyEnrolled = enrolledMemberIds.has(p.member_id);
                  const isSelected = selectedIds.has(p.member_id);
                  return (
                    <div
                      key={p.member_id}
                      className={`flex items-center justify-between py-2.5 px-2 rounded-lg mb-1 transition-colors ${alreadyEnrolled ? "opacity-40" : isSelected ? "bg-[#1B4D3E]/5" : "hover:bg-gray-50"}`}
                      data-testid={`enroll-player-${p.member_id}`}
                    >
                      <div className="flex items-center gap-3">
                        {!alreadyEnrolled && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => togglePlayer(p.member_id)}
                            className="w-4 h-4 rounded border-gray-300 text-[#1B4D3E]"
                          />
                        )}
                        <div>
                          <p className="text-sm font-medium text-[#0F172A]">
                            {p.first_name} {p.last_name}
                            {p.sex === "F" && <span className="ml-2 text-[10px] font-semibold bg-pink-100 text-pink-700 px-1.5 py-0.5 rounded">D</span>}
                            {p.is_senior && <span className="ml-1 text-[10px] font-semibold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">S</span>}
                          </p>
                          <p className="text-xs text-[#94A3B8]">
                            {p.member_number && `#${p.member_number} `}
                            {p.handicap_index != null ? `HI: ${p.handicap_index < 0 ? "+" + Math.abs(p.handicap_index) : p.handicap_index}` : "Sin index"}
                          </p>
                        </div>
                      </div>
                      {alreadyEnrolled && <span className="text-xs text-green-600 font-medium">Inscrito</span>}
                    </div>
                  );
                })}
                {searchResults.length === 0 && <p className="text-sm text-[#94A3B8] text-center py-6">Sin resultados con esos filtros</p>}
              </div>

              <div className="px-5 py-3 border-t border-gray-100 flex flex-col sm:flex-row gap-2">
                <Button variant="outline" className="sm:flex-1" onClick={() => setShowSearch(false)}>Cancelar</Button>
                {eligible.length > 0 && selectedIds.size === 0 && (
                  <Button
                    variant="outline"
                    className="sm:flex-1 border-[#1B4D3E] text-[#1B4D3E] hover:bg-[#1B4D3E]/5"
                    disabled={enrolling}
                    onClick={enrollAllFiltered}
                    data-testid="enroll-all-filtered-btn"
                  >
                    Inscribir a los {eligible.length} filtrados
                  </Button>
                )}
                <Button
                  className="sm:flex-1 bg-[#1B4D3E] hover:bg-[#163F33] text-white"
                  disabled={selectedIds.size === 0 || enrolling}
                  onClick={handleBulkEnroll}
                  data-testid="confirm-enroll-btn"
                >
                  {enrolling ? "Inscribiendo..." : `Inscribir ${selectedIds.size || ""} seleccionado(s)`}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Enrolled list */}
        {loading ? (
          <div className="flex justify-center py-6"><div className="w-6 h-6 border-3 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" /></div>
        ) : enrollments.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-sm text-[#94A3B8]">No hay jugadores inscritos</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="enrollments-table">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left px-3 py-2 text-xs font-semibold text-[#64748B] uppercase">Jugador</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-[#64748B] uppercase">Index</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-[#64748B] uppercase">Categoria</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {enrollments.map((e) => (
                  <tr key={e.enrollment_id} className="border-b border-gray-50 hover:bg-gray-50/50" data-testid={`enrollment-${e.enrollment_id}`}>
                    <td className="px-3 py-2">
                      <p className="font-medium text-[#0F172A]">{e.player_name}</p>
                      {e.player_member_number && <p className="text-xs text-[#94A3B8]">#{e.player_member_number}</p>}
                    </td>
                    <td className="px-3 py-2">
                      {e.handicap_index != null ? (
                        <span className={`font-semibold ${e.handicap_index < 0 ? "text-blue-600" : ""}`}>
                          {e.handicap_index < 0 ? `+${Math.abs(e.handicap_index)}` : e.handicap_index}
                        </span>
                      ) : <span className="text-[#94A3B8]">-</span>}
                    </td>
                    <td className="px-3 py-2">
                      {e.category_name ? (
                        <span className="text-xs bg-[#1B4D3E]/10 text-[#1B4D3E] px-2 py-0.5 rounded-full font-medium">{e.category_name}</span>
                      ) : <span className="text-xs text-[#94A3B8]">Sin cat.</span>}
                    </td>
                    <td className="px-3 py-2">
                      {canWrite && (
                        <Button variant="ghost" size="sm" onClick={() => handleRemove(e.enrollment_id)} data-testid={`remove-enrollment-${e.enrollment_id}`}>
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ==========================================================================
// Tee Times Section
// ==========================================================================

function TeeTimesSection({ stage, rankingId }) {
  const ctx = useClub();
  const canWrite = ["master", "operador"].includes(ctx?.clubUser?.club_role || "");
  const [teetimes, setTeetimes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerate, setShowGenerate] = useState(false);
  const [clubSlots, setClubSlots] = useState([]);
  const [genForm, setGenForm] = useState({
    day: "1",
    selectedSlotIds: new Set(),
    players_per_group: 4,
    sort_by: "random",
    group_by_category: false,
    fill_mode: "strict",
  });
  const [generating, setGenerating] = useState(false);
  const [editingTT, setEditingTT] = useState(null);
  const [editGroups, setEditGroups] = useState([]);
  const [savingGroups, setSavingGroups] = useState(false);

  const fetchTT = useCallback(async () => {
    try {
      const res = await fetch(`${API}/club/rankings/${rankingId}/stages/${stage.stage_id}/teetimes`, {
        credentials: "include", headers: AUTH(),
      });
      if (res.ok) setTeetimes(await res.json());
    } catch { /* silent */ } finally { setLoading(false); }
  }, [stage.stage_id, rankingId]);

  useEffect(() => { fetchTT(); }, [fetchTT]);

  const openGenerate = async () => {
    setShowGenerate(true);
    try {
      const res = await fetch(`${API}/club/tee-slots`, { credentials: "include", headers: AUTH() });
      if (res.ok) {
        const slots = await res.json();
        setClubSlots(slots);
        // Pre-select all slots by default
        setGenForm((prev) => ({ ...prev, selectedSlotIds: new Set(slots.map((s) => s.slot_id)) }));
      }
    } catch { /* silent */ }
  };

  const toggleSlot = (slotId) => {
    setGenForm((prev) => {
      const next = new Set(prev.selectedSlotIds);
      if (next.has(slotId)) next.delete(slotId);
      else next.add(slotId);
      return { ...prev, selectedSlotIds: next };
    });
  };

  const selectAll = () => setGenForm((p) => ({ ...p, selectedSlotIds: new Set(clubSlots.map((s) => s.slot_id)) }));
  const clearAll = () => setGenForm((p) => ({ ...p, selectedSlotIds: new Set() }));

  const handleGenerate = async () => {
    if (genForm.selectedSlotIds.size === 0) {
      toast.error("Selecciona al menos un horario");
      return;
    }
    setGenerating(true);
    try {
      const res = await fetch(`${API}/club/rankings/${rankingId}/stages/${stage.stage_id}/teetimes/generate`, {
        method: "POST", credentials: "include", headers: AUTH(),
        body: JSON.stringify({
          day: parseInt(genForm.day) || 1,
          slot_ids: Array.from(genForm.selectedSlotIds),
          players_per_group: genForm.players_per_group,
          sort_by: genForm.sort_by,
          group_by_category: genForm.group_by_category,
          fill_mode: genForm.fill_mode,
        }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || "Error"); }
      toast.success("Tee times generados");
      setShowGenerate(false);
      fetchTT();
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Error al generar");
    } finally { setGenerating(false); }
  };

  const handlePublish = async (ttId, newStatus) => {
    try {
      await fetch(`${API}/club/rankings/${rankingId}/stages/${stage.stage_id}/teetimes/${ttId}`, {
        method: "PUT", credentials: "include", headers: AUTH(),
        body: JSON.stringify({ status: newStatus }),
      });
      toast.success(newStatus === "published" ? "Tee times publicados" : "Tee times despublicados");
      fetchTT();
    } catch { toast.error("Error"); }
  };

  const startEditGroups = (tt) => {
    setEditingTT(tt.tee_time_id);
    setEditGroups(JSON.parse(JSON.stringify(tt.groups || [])));
  };

  const movePlayer = (fromGroupIdx, playerIdx, toGroupIdx) => {
    setEditGroups((prev) => {
      const next = prev.map((g) => ({ ...g, players: [...g.players] }));
      const [player] = next[fromGroupIdx].players.splice(playerIdx, 1);
      next[toGroupIdx].players.push(player);
      return next;
    });
  };

  const updateGroupField = (groupIdx, field, value) => {
    setEditGroups((prev) => prev.map((g, i) => i === groupIdx ? { ...g, [field]: value } : g));
  };

  const handleSaveGroups = async () => {
    setSavingGroups(true);
    try {
      const cleaned = editGroups
        .filter((g) => g.players.length > 0)
        .map((g, i) => ({ ...g, group_number: i + 1, hole: parseInt(g.hole) || 1 }));
      const res = await fetch(`${API}/club/rankings/${rankingId}/stages/${stage.stage_id}/teetimes/${editingTT}`, {
        method: "PUT", credentials: "include", headers: AUTH(),
        body: JSON.stringify({ groups: cleaned }),
      });
      if (!res.ok) throw new Error("Error");
      toast.success("Grupos actualizados");
      setEditingTT(null);
      fetchTT();
    } catch { toast.error("Error al guardar grupos"); }
    finally { setSavingGroups(false); }
  };

  return (
    <Card className="card-default mb-4">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-[#0F172A]">Tee Times</h3>
          {canWrite && (
            <Button size="sm" onClick={openGenerate} className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" data-testid="generate-tt-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Generar Tee Times
            </Button>
          )}
        </div>

        {/* Generate form */}
        {showGenerate && (
          <div className="mb-4 p-4 bg-gray-50 rounded-xl border border-gray-200" data-testid="generate-tt-form">
            <h4 className="font-bold text-[#0F172A] text-sm mb-3">Configurar Generacion</h4>

            {clubSlots.length === 0 ? (
              <div className="text-center py-6 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-900 font-medium mb-1">No hay horarios del club configurados</p>
                <p className="text-xs text-amber-700">Ve a "Tee Times" en el menu y agrega tus horarios base primero.</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Dia de Juego</Label>
                    <Select value={genForm.day} onValueChange={(v) => setGenForm({ ...genForm, day: v })}>
                      <SelectTrigger data-testid="tt-day"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: stage.play_days || 1 }, (_, i) => (
                          <SelectItem key={i + 1} value={String(i + 1)}>Dia {i + 1}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Jugadores / Grupo</Label>
                    <Select value={String(genForm.players_per_group)} onValueChange={(v) => setGenForm({ ...genForm, players_per_group: parseInt(v) })}>
                      <SelectTrigger data-testid="tt-ppg"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Ordenamiento</Label>
                    <Select value={genForm.sort_by} onValueChange={(v) => setGenForm({ ...genForm, sort_by: v })}>
                      <SelectTrigger data-testid="tt-sort"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="random">Aleatorio</SelectItem>
                        <SelectItem value="by_handicap">Por Handicap Index</SelectItem>
                        <SelectItem value="by_ranking">Por Ranking</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Si sobran jugadores</Label>
                    <Select value={genForm.fill_mode} onValueChange={(v) => setGenForm({ ...genForm, fill_mode: v })}>
                      <SelectTrigger data-testid="tt-fill-mode"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="strict">Grupo aparte con los sobrantes</SelectItem>
                        <SelectItem value="balanced">Repartir en los últimos grupos (ej. grupos de {Math.min(genForm.players_per_group + 1, 5)})</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Agrupar por Categoria</Label>
                    <div className="flex items-center gap-2 mt-2">
                      <input
                        type="checkbox"
                        checked={genForm.group_by_category}
                        onChange={(e) => setGenForm({ ...genForm, group_by_category: e.target.checked })}
                        className="w-4 h-4 rounded border-gray-300 text-[#1B4D3E]"
                        data-testid="tt-group-cat"
                      />
                      <span className="text-sm text-[#64748B]">Separar por categoria</span>
                    </div>
                  </div>
                </div>

                {/* Slot checkboxes */}
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs font-medium">
                      Horarios a usar ({genForm.selectedSlotIds.size}/{clubSlots.length} seleccionados ·
                      capacidad: {genForm.selectedSlotIds.size * genForm.players_per_group} jugadores)
                    </Label>
                    <div className="flex gap-1">
                      <button type="button" onClick={selectAll} className="text-xs text-[#1B4D3E] hover:underline" data-testid="tt-select-all">Todos</button>
                      <span className="text-xs text-gray-300">|</span>
                      <button type="button" onClick={clearAll} className="text-xs text-[#64748B] hover:underline" data-testid="tt-clear-all">Ninguno</button>
                    </div>
                  </div>
                  <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg bg-white p-2 grid grid-cols-2 sm:grid-cols-3 gap-1" data-testid="tt-slot-checkboxes">
                    {clubSlots.map((s) => {
                      const checked = genForm.selectedSlotIds.has(s.slot_id);
                      return (
                        <label
                          key={s.slot_id}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm ${checked ? "bg-[#1B4D3E]/10" : "hover:bg-gray-50"}`}
                          data-testid={`tt-slot-${s.slot_id}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleSlot(s.slot_id)}
                            className="w-4 h-4 rounded border-gray-300 text-[#1B4D3E]"
                          />
                          <span className="font-medium text-[#0F172A]">{s.time}</span>
                          <span className="text-xs text-[#64748B]">H{s.hole}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            <div className="flex gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={() => setShowGenerate(false)}>Cancelar</Button>
              <Button
                size="sm"
                className="bg-[#1B4D3E] hover:bg-[#163F33] text-white"
                onClick={handleGenerate}
                disabled={generating || clubSlots.length === 0}
                data-testid="confirm-generate-btn"
              >
                {generating ? "Generando..." : "Generar"}
              </Button>
            </div>
          </div>
        )}

        {/* Tee times list */}
        {loading ? (
          <div className="flex justify-center py-6"><div className="w-6 h-6 border-3 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" /></div>
        ) : teetimes.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-sm text-[#94A3B8]">No hay tee times generados</p>
          </div>
        ) : (
          teetimes.map((tt) => (
            <div key={tt.tee_time_id} className="mb-4" data-testid={`tt-day-${tt.day}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <h4 className="font-bold text-sm text-[#0F172A]">Dia {tt.day}</h4>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${tt.status === "published" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                    {tt.status === "published" ? "Publicado" : "Borrador"}
                  </span>
                  <span className="text-xs text-[#94A3B8]">{tt.total_groups} grupos, {tt.total_players} jugadores</span>
                </div>
                {canWrite && (
                  <div className="flex gap-2">
                    {editingTT !== tt.tee_time_id && (
                      <Button variant="outline" size="sm" onClick={() => startEditGroups(tt)} data-testid={`tt-edit-groups-${tt.tee_time_id}`}>
                        <Pencil className="w-3.5 h-3.5 mr-1" /> Editar Grupos
                      </Button>
                    )}
                    <Button
                      variant="outline" size="sm"
                      onClick={() => handlePublish(tt.tee_time_id, tt.status === "published" ? "draft" : "published")}
                      data-testid={`tt-publish-${tt.tee_time_id}`}
                    >
                      {tt.status === "published" ? "Despublicar" : "Publicar"}
                    </Button>
                  </div>
                )}
              </div>
              {editingTT === tt.tee_time_id ? (
                <div className="space-y-2" data-testid={`tt-edit-groups-panel-${tt.day}`}>
                  <p className="text-xs text-[#94A3B8]">Usa el selector junto a cada jugador para moverlo de grupo. También puedes cambiar hora y hoyo de salida. Los grupos vacíos se eliminan al guardar.</p>
                  {editGroups.map((g, gi) => (
                    <div key={gi} className="p-3 bg-white rounded-lg border border-[#1B4D3E]/20">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-xs font-bold text-[#1B4D3E]">Grupo {gi + 1}</span>
                        <div className="flex items-center gap-1">
                          <Label className="text-[10px] text-[#94A3B8]">Hora</Label>
                          <Input value={g.time} onChange={(e) => updateGroupField(gi, "time", e.target.value)} className="w-20 h-7 text-xs text-center" data-testid={`tt-group-time-${gi}`} />
                        </div>
                        <div className="flex items-center gap-1">
                          <Label className="text-[10px] text-[#94A3B8]">Hoyo</Label>
                          <Input type="number" value={g.hole} onChange={(e) => updateGroupField(gi, "hole", e.target.value)} className="w-14 h-7 text-xs text-center" data-testid={`tt-group-hole-${gi}`} />
                        </div>
                        <span className="text-xs text-[#94A3B8] ml-auto">{g.players.length} jugador{g.players.length !== 1 ? "es" : ""}</span>
                      </div>
                      <div className="space-y-1">
                        {g.players.length === 0 ? (
                          <p className="text-xs text-[#94A3B8] italic py-1">Grupo vacío (se eliminará al guardar)</p>
                        ) : g.players.map((p, pi) => (
                          <div key={p.enrollment_id || p.member_id} className="flex items-center gap-2 text-sm bg-gray-50 rounded px-2 py-1">
                            <span className="text-[#0F172A] flex-1 truncate">{p.name}</span>
                            {p.category_name && (
                              <span className="text-xs bg-[#1B4D3E]/10 text-[#1B4D3E] px-1.5 py-0.5 rounded">{p.category_name}</span>
                            )}
                            <select
                              value={gi}
                              onChange={(e) => movePlayer(gi, pi, parseInt(e.target.value))}
                              className="h-7 text-xs border border-gray-200 rounded px-1 bg-white"
                              data-testid={`tt-move-player-${p.enrollment_id || p.member_id}`}
                            >
                              {editGroups.map((_, oi) => (
                                <option key={oi} value={oi}>Grupo {oi + 1}</option>
                              ))}
                            </select>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  <div className="flex gap-2 pt-1">
                    <Button variant="outline" size="sm" onClick={() => setEditingTT(null)}>Cancelar</Button>
                    <Button size="sm" className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" onClick={handleSaveGroups} disabled={savingGroups} data-testid="tt-save-groups-btn">
                      {savingGroups ? "Guardando..." : "Guardar Grupos"}
                    </Button>
                  </div>
                </div>
              ) : (
              <div className="space-y-2" data-testid={`tt-groups-${tt.day}`}>
                {(tt.groups || []).map((g) => (
                  <div key={g.group_number} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <div className="min-w-[55px] text-center">
                      <p className="text-xs text-[#94A3B8]">Grupo {g.group_number}</p>
                      <p className="font-bold text-[#0F172A]">{g.time}</p>
                      <p className="text-xs text-[#94A3B8]">Hoyo {g.hole}</p>
                    </div>
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-1">
                      {g.players.map((p) => (
                        <div key={p.member_id} className="flex items-center gap-2 text-sm">
                          <span className="text-[#0F172A]">{p.name}</span>
                          {p.handicap_index != null && (
                            <span className="text-xs text-[#94A3B8]">
                              ({p.handicap_index < 0 ? `+${Math.abs(p.handicap_index)}` : p.handicap_index})
                            </span>
                          )}
                          {p.category_name && (
                            <span className="text-xs bg-[#1B4D3E]/10 text-[#1B4D3E] px-1.5 py-0.5 rounded">{p.category_name}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              )}
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}


// ==========================================================================
// Scores Section (capture + stage leaderboard)
// ==========================================================================

function ScoresSection({ stage, rankingId, onFinalized }) {
  const ctx = useClub();
  const canWrite = ["master", "operador"].includes(ctx?.clubUser?.club_role || "");
  const canCapture = canWrite || ctx?.clubUser?.club_role === "capturista";
  const [enrollments, setEnrollments] = useState([]);
  const [scores, setScores] = useState({});
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCapture, setShowCapture] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [showEditResults, setShowEditResults] = useState(false);
  const [editResults, setEditResults] = useState({});

  const playDays = stage.play_days || 1;

  const fetchData = useCallback(async () => {
    try {
      const [enrRes, scRes, lbRes] = await Promise.all([
        fetch(`${API}/club/rankings/${rankingId}/stages/${stage.stage_id}/enrollments`, { credentials: "include", headers: AUTH() }),
        fetch(`${API}/club/rankings/${rankingId}/stages/${stage.stage_id}/scores`, { credentials: "include", headers: AUTH() }),
        fetch(`${API}/club/rankings/${rankingId}/stages/${stage.stage_id}/leaderboard`, { credentials: "include", headers: AUTH() }),
      ]);
      if (enrRes.ok) setEnrollments(await enrRes.json());
      if (scRes.ok) {
        const scData = await scRes.json();
        const map = {};
        scData.forEach((s) => { map[s.enrollment_id] = s; });
        setScores(map);
      }
      if (lbRes.ok) setLeaderboard(await lbRes.json());
    } catch { /* silent */ } finally { setLoading(false); }
  }, [stage.stage_id, rankingId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const [editScores, setEditScores] = useState({});

  const initEditScores = () => {
    const init = {};
    enrollments.forEach((e) => {
      const existing = scores[e.enrollment_id];
      init[e.enrollment_id] = existing?.rounds || Array(playDays).fill("");
    });
    setEditScores(init);
    setShowCapture(true);
  };

  const updateRound = (enrId, roundIdx, value) => {
    setEditScores((prev) => {
      const rounds = [...(prev[enrId] || Array(playDays).fill(""))];
      rounds[roundIdx] = value;
      return { ...prev, [enrId]: rounds };
    });
  };

  const handleSaveScores = async () => {
    setSaving(true);
    try {
      const payload = Object.entries(editScores).map(([enrId, rounds]) => ({
        enrollment_id: enrId,
        rounds: rounds.map((r) => r === "" || r === null ? null : parseInt(r)),
      }));
      const res = await fetch(`${API}/club/rankings/${rankingId}/stages/${stage.stage_id}/scores`, {
        method: "PUT", credentials: "include", headers: AUTH(),
        body: JSON.stringify({ scores: payload }),
      });
      if (!res.ok) throw new Error("Error");
      toast.success("Scores guardados");
      setShowCapture(false);
      fetchData();
    } catch { toast.error("Error al guardar scores"); }
    finally { setSaving(false); }
  };

  const handleFinalize = async () => {
    const isRefinalize = stage.status === "completed";
    if (!window.confirm(isRefinalize
      ? "Re-finalizar etapa? Se recalcularan posiciones y puntos a partir de los scores actuales."
      : "Finalizar etapa? Esto cerrara los resultados y asignara puntos al ranking.")) return;
    setFinalizing(true);
    try {
      const res = await fetch(`${API}/club/rankings/${rankingId}/stages/${stage.stage_id}/finalize`, {
        method: "POST", credentials: "include", headers: AUTH(),
      });
      if (!res.ok) throw new Error("Error");
      toast.success("Etapa finalizada y puntos asignados");
      fetchData();
      onFinalized();
    } catch { toast.error("Error al finalizar"); }
    finally { setFinalizing(false); }
  };

  const initEditResults = () => {
    const init = {};
    Object.values(leaderboard?.categories || {}).forEach((players) => {
      players.forEach((p) => {
        init[p.enrollment_id] = {
          player_name: p.player_name,
          category_name: p.category_name,
          position: p.position ?? "",
          total_score: p.total_score ?? "",
          points: p.points ?? "",
          status: p.status || "",
        };
      });
    });
    setEditResults(init);
    setShowEditResults(true);
  };

  const updateResultField = (enrId, field, value) => {
    setEditResults((prev) => ({ ...prev, [enrId]: { ...prev[enrId], [field]: value } }));
  };

  const handleSaveResults = async () => {
    setSaving(true);
    try {
      const payload = Object.entries(editResults).map(([enrId, r]) => ({
        enrollment_id: enrId,
        position: r.position === "" ? null : parseInt(r.position),
        total_score: r.total_score === "" ? null : parseInt(r.total_score),
        points: r.points === "" ? 0 : parseFloat(r.points),
        status: r.status || null,
      }));
      const res = await fetch(`${API}/club/rankings/${rankingId}/stages/${stage.stage_id}/results`, {
        method: "PUT", credentials: "include", headers: AUTH(),
        body: JSON.stringify({ results: payload }),
      });
      if (!res.ok) throw new Error("Error");
      toast.success("Resultados actualizados");
      setShowEditResults(false);
      fetchData();
      onFinalized();
    } catch { toast.error("Error al guardar resultados"); }
    finally { setSaving(false); }
  };

  const handleDeleteResult = async (enrId, name) => {
    if (!window.confirm(`Eliminar el resultado de ${name}? Sus puntos se restaran de la clasificación general.`)) return;
    try {
      const res = await fetch(`${API}/club/rankings/${rankingId}/stages/${stage.stage_id}/results/${enrId}`, {
        method: "DELETE", credentials: "include", headers: AUTH(),
      });
      if (!res.ok) throw new Error("Error");
      setEditResults((prev) => { const next = { ...prev }; delete next[enrId]; return next; });
      toast.success("Resultado eliminado");
      fetchData();
      onFinalized();
    } catch { toast.error("Error al eliminar resultado"); }
  };

  const handleCsvScores = async (csvText, mode) => {
    try {
      const res = await fetch(`${API}/club/rankings/${rankingId}/stages/${stage.stage_id}/scores/csv`, {
        method: "POST", credentials: "include", headers: AUTH(),
        body: JSON.stringify({ csv: csvText, mode }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error al importar");
      toast.success(`${data.saved} resultado(s) importado(s)${data.enrolled_new ? `, ${data.enrolled_new} inscrito(s) nuevo(s)` : ""}${data.created_members ? `, ${data.created_members} alta(s) en padrón` : ""}`);
      if (data.errors?.length) toast.warning(`${data.errors.length} fila(s) con error: ${data.errors[0]}`);
      fetchData();
      return true;
    } catch (err) {
      toast.error(err.message);
      return false;
    }
  };

  const handleCsvDirectPoints = async (csvText) => {
    try {
      const res = await fetch(`${API}/club/rankings/${rankingId}/stages/${stage.stage_id}/results/csv`, {
        method: "POST", credentials: "include", headers: AUTH(),
        body: JSON.stringify({ csv: csvText }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error al importar");
      toast.success(`${data.saved} jugador(es) con puntos asignados${data.created_members ? `, ${data.created_members} alta(s) en padrón` : ""}. Etapa finalizada.`);
      if (data.errors?.length) toast.warning(`${data.errors.length} fila(s) con error: ${data.errors[0]}`);
      fetchData();
      if (data.stage_completed) onFinalized();
      return true;
    } catch (err) {
      toast.error(err.message);
      return false;
    }
  };

  return (
    <Card className="card-default mb-4">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-[#0F172A]">Resultados</h3>
          <div className="flex gap-2 flex-wrap justify-end">
            {canWrite && !showCapture && !showEditResults && (
              <CsvImportButton
                label="Puntos Directos"
                columnsHelp={"Para eventos externos (ej. torneo fuera del club) que suman puntos a la clasificación general SIN capturar inscripciones, tee times ni scores. Columnas: nombre (o jugador), apellido, email, socio, puntos (obligatoria), posicion y score (opcionales). Acepta WD/NS/DQ en puntos. Al importar, la etapa se marca como finalizada."}
                templates={[{ label: "Plantilla puntos directos", filename: "puntos_directos.csv", content: "nombre,apellido,email,socio,puntos,posicion,score\nJuan,Perez,juan@ejemplo.com,1024,1000,1,70\nMaria,Lopez,,2048,600,2,72\n" }]}
                onImport={handleCsvDirectPoints}
                testId="direct-points-csv"
              />
            )}
            {canWrite && !showCapture && !showEditResults && (
              <CsvImportButton
                label="Importar CSV"
                modes={[
                  { id: "total", label: "Score Total", desc: playDays > 1 ? `Una columna por ronda (r1..r${playDays})` : "Una columna con el total de golpes" },
                  { id: "holes", label: "Hoyo por Hoyo", desc: "Columnas h1 a h18" },
                ]}
                columnsHelp={`Identifica al jugador con: nombre (o jugador), apellido, email o socio. Esta etapa tiene ${playDays} ronda(s): en modo Score Total usa una columna por ronda (r1${playDays > 1 ? `..r${playDays}` : ""}). Acepta WD (retiro), NS (no se presentó) y DQ (descalificado) — esos jugadores no suman puntos. Jugadores no inscritos se inscriben automáticamente.`}
                templates={[
                  { mode: "total", label: "Plantilla score total", filename: "resultados_total.csv", content: `nombre,apellido,email,socio,handicap,${Array.from({ length: playDays }, (_, i) => `r${i + 1}`).join(",")}\nJuan,Perez,juan@ejemplo.com,1024,12.4,${Array.from({ length: playDays }, () => "82").join(",")}\nMaria,Lopez,,2048,8.1,${Array.from({ length: playDays }, () => "WD").join(",")}\n` },
                  { mode: "holes", label: "Plantilla hoyo por hoyo", filename: "resultados_hoyos.csv", content: "nombre,apellido,socio,h1,h2,h3,h4,h5,h6,h7,h8,h9,h10,h11,h12,h13,h14,h15,h16,h17,h18\nJuan,Perez,1024,4,5,3,4,4,5,4,3,5,4,4,5,4,4,3,4,5,4\n" },
                ]}
                onImport={handleCsvScores}
                testId="scores-csv"
              />
            )}
            {canCapture && !showCapture && !showEditResults && (
              <Button size="sm" onClick={initEditScores} className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" data-testid="capture-scores-btn">
                <Pencil className="w-3.5 h-3.5 mr-1" /> Capturar Scores
              </Button>
            )}
            {canWrite && stage.status === "completed" && !showCapture && !showEditResults && leaderboard?.total_players > 0 && (
              <Button size="sm" variant="outline" onClick={initEditResults} data-testid="edit-results-btn">
                <Pencil className="w-3.5 h-3.5 mr-1" /> Editar Resultados
              </Button>
            )}
            {canWrite && !showCapture && !showEditResults && (
              <Button size="sm" variant="outline" onClick={handleFinalize} disabled={finalizing} data-testid="finalize-stage-btn">
                {finalizing ? "Finalizando..." : stage.status === "completed" ? "Re-finalizar Etapa" : "Finalizar Etapa"}
              </Button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-6"><div className="w-6 h-6 border-3 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" /></div>
        ) : showEditResults ? (
          /* Editable results grid */
          <div data-testid="edit-results-grid">
            <p className="text-xs text-[#94A3B8] mb-3">Modifica posición, score, puntos o estatus (WD/NS/DQ) de cada jugador. Los cambios se reflejan en la clasificación general.</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left px-2 py-2 text-xs font-semibold text-[#64748B]">Jugador</th>
                    <th className="text-left px-2 py-2 text-xs font-semibold text-[#64748B]">Cat.</th>
                    <th className="text-center px-2 py-2 text-xs font-semibold text-[#64748B]">Pos</th>
                    <th className="text-center px-2 py-2 text-xs font-semibold text-[#64748B]">Score</th>
                    <th className="text-center px-2 py-2 text-xs font-semibold text-[#1B4D3E]">Pts</th>
                    <th className="text-center px-2 py-2 text-xs font-semibold text-[#64748B]">Estatus</th>
                    <th className="px-2 py-2 w-10" />
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(editResults).map(([enrId, r]) => (
                    <tr key={enrId} className="border-b border-gray-50">
                      <td className="px-2 py-1.5 font-medium text-[#0F172A] text-xs">{r.player_name}</td>
                      <td className="px-2 py-1.5">
                        {r.category_name && r.category_name !== "Sin Categoria" && (
                          <span className="text-xs bg-[#1B4D3E]/10 text-[#1B4D3E] px-1.5 py-0.5 rounded">{r.category_name}</span>
                        )}
                      </td>
                      <td className="px-1 py-1">
                        <Input type="number" value={r.position} onChange={(ev) => updateResultField(enrId, "position", ev.target.value)} className="w-14 text-center text-sm h-8" data-testid={`result-pos-${enrId}`} />
                      </td>
                      <td className="px-1 py-1">
                        <Input type="number" value={r.total_score} onChange={(ev) => updateResultField(enrId, "total_score", ev.target.value)} className="w-16 text-center text-sm h-8" data-testid={`result-score-${enrId}`} />
                      </td>
                      <td className="px-1 py-1">
                        <Input type="number" value={r.points} onChange={(ev) => updateResultField(enrId, "points", ev.target.value)} className="w-20 text-center text-sm h-8 font-bold" data-testid={`result-points-${enrId}`} />
                      </td>
                      <td className="px-1 py-1">
                        <select
                          value={r.status}
                          onChange={(ev) => updateResultField(enrId, "status", ev.target.value)}
                          className="h-8 text-xs border border-gray-200 rounded-md px-1 bg-white"
                          data-testid={`result-status-${enrId}`}
                        >
                          <option value="">—</option>
                          <option value="WD">WD</option>
                          <option value="NS">NS</option>
                          <option value="DQ">DQ</option>
                        </select>
                      </td>
                      <td className="px-1 py-1 text-center">
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteResult(enrId, r.player_name)} data-testid={`result-delete-${enrId}`}>
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={() => setShowEditResults(false)}>Cancelar</Button>
              <Button size="sm" className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" onClick={handleSaveResults} disabled={saving} data-testid="save-results-btn">
                {saving ? "Guardando..." : "Guardar Resultados"}
              </Button>
            </div>
          </div>
        ) : showCapture ? (
          /* Score capture grid */
          <div data-testid="score-capture-grid">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left px-2 py-2 text-xs font-semibold text-[#64748B]">Jugador</th>
                    <th className="text-left px-2 py-2 text-xs font-semibold text-[#64748B]">Cat.</th>
                    {Array.from({ length: playDays }, (_, i) => (
                      <th key={i} className="text-center px-2 py-2 text-xs font-semibold text-[#64748B]">R{i + 1}</th>
                    ))}
                    <th className="text-center px-2 py-2 text-xs font-semibold text-[#64748B]">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {enrollments.map((e) => {
                    const rounds = editScores[e.enrollment_id] || Array(playDays).fill("");
                    const total = rounds.reduce((s, r) => s + (parseInt(r) || 0), 0);
                    return (
                      <tr key={e.enrollment_id} className="border-b border-gray-50">
                        <td className="px-2 py-1.5 font-medium text-[#0F172A] text-xs">{e.player_name}</td>
                        <td className="px-2 py-1.5">
                          {e.category_name && <span className="text-xs bg-[#1B4D3E]/10 text-[#1B4D3E] px-1.5 py-0.5 rounded">{e.category_name}</span>}
                        </td>
                        {Array.from({ length: playDays }, (_, i) => (
                          <td key={i} className="px-1 py-1">
                            <Input
                              type="number"
                              value={rounds[i] ?? ""}
                              onChange={(ev) => updateRound(e.enrollment_id, i, ev.target.value)}
                              className="w-16 text-center text-sm h-8"
                              data-testid={`score-${e.enrollment_id}-r${i + 1}`}
                            />
                          </td>
                        ))}
                        <td className="px-2 py-1.5 text-center font-bold text-[#0F172A]">{total || "-"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={() => setShowCapture(false)}>Cancelar</Button>
              <Button size="sm" className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" onClick={handleSaveScores} disabled={saving} data-testid="save-scores-btn">
                {saving ? "Guardando..." : "Guardar Scores"}
              </Button>
            </div>
          </div>
        ) : leaderboard && leaderboard.total_players > 0 ? (
          /* Stage Leaderboard */
          <div data-testid="stage-leaderboard">
            {leaderboard.multiplier !== 1.0 && (
              <p className="text-xs text-amber-600 font-medium mb-3">Multiplicador: x{leaderboard.multiplier}</p>
            )}
            {Object.entries(leaderboard.categories).map(([catName, players]) => (
              <div key={catName} className="mb-4">
                <h4 className="text-sm font-bold text-[#0F172A] mb-2 flex items-center gap-2">
                  <span className="bg-[#1B4D3E]/10 text-[#1B4D3E] px-2 py-0.5 rounded-full text-xs">{catName}</span>
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-center px-2 py-1.5 font-semibold text-[#64748B] w-10">Pos</th>
                        <th className="text-left px-2 py-1.5 font-semibold text-[#64748B]">Jugador</th>
                        {players[0]?.rounds?.map((_, i) => (
                          <th key={i} className="text-center px-2 py-1.5 font-semibold text-[#64748B]">R{i + 1}</th>
                        ))}
                        <th className="text-center px-2 py-1.5 font-semibold text-[#64748B]">Total</th>
                        <th className="text-center px-2 py-1.5 font-semibold text-[#1B4D3E]">Pts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {players.map((p) => (
                        <tr key={p.enrollment_id} className={`border-b border-gray-50 hover:bg-gray-50/50 ${p.status ? "opacity-60" : ""}`}>
                          <td className="text-center px-2 py-1.5 font-bold text-[#0F172A]">
                            {p.status ? (
                              <span className="text-[10px] font-black bg-red-100 text-red-600 px-1.5 py-0.5 rounded" data-testid={`status-${p.enrollment_id}`}>{p.status}</span>
                            ) : p.position}
                          </td>
                          <td className="px-2 py-1.5 font-medium text-[#0F172A]">{p.player_name}</td>
                          {(p.rounds || []).map((r, i) => (
                            <td key={i} className="text-center px-2 py-1.5 text-[#64748B]">{r ?? "-"}</td>
                          ))}
                          <td className="text-center px-2 py-1.5 font-bold text-[#0F172A]">{p.total_score ?? "-"}</td>
                          <td className="text-center px-2 py-1.5 font-bold text-[#1B4D3E]">{p.points || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6">
            <p className="text-sm text-[#94A3B8]">Sin resultados capturados</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ==========================================================================
// Ranking Leaderboard (accumulated)
// ==========================================================================

function RankingLeaderboard({ rankingId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch_ = async () => {
      try {
        const res = await fetch(`${API}/club/rankings/${rankingId}/leaderboard`, {
          credentials: "include", headers: AUTH(),
        });
        if (res.ok) setData(await res.json());
      } catch { /* silent */ } finally { setLoading(false); }
    };
    fetch_();
  }, [rankingId]);

  if (loading) return null;
  if (!data || Object.keys(data.categories || {}).length === 0) return null;

  return (
    <Card className="card-default mb-6" data-testid="ranking-leaderboard">
      <CardContent className="p-6">
        <h2 className="text-base font-bold text-[#0F172A] mb-1">Clasificacion General</h2>
        <p className="text-xs text-[#94A3B8] mb-4">{data.total_stages_completed} etapa(s) finalizadas</p>

        {Object.entries(data.categories).map(([catName, entries]) => (
          <div key={catName} className="mb-5">
            <h3 className="text-sm font-bold text-[#0F172A] mb-2">
              <span className="bg-[#1B4D3E]/10 text-[#1B4D3E] px-2 py-0.5 rounded-full text-xs">{catName}</span>
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-center px-2 py-1.5 font-semibold text-[#64748B] w-10">Pos</th>
                    <th className="text-left px-2 py-1.5 font-semibold text-[#64748B]">Jugador</th>
                    <th className="text-center px-2 py-1.5 font-semibold text-[#64748B]">Etapas</th>
                    <th className="text-center px-2 py-1.5 font-semibold text-[#1B4D3E]">Puntos</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <tr key={e.member_id} className="border-b border-gray-50 hover:bg-gray-50/50">
                      <td className="text-center px-2 py-1.5 font-bold text-[#0F172A]">{e.ranking_position}</td>
                      <td className="px-2 py-1.5 font-medium text-[#0F172A]">{e.player_name}</td>
                      <td className="text-center px-2 py-1.5 text-[#64748B]">{e.stages_played}</td>
                      <td className="text-center px-2 py-1.5 font-bold text-[#1B4D3E]">{e.total_points}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
